import React, { useState, useEffect, useRef } from 'react';
import './embroidery_style.css';
import './embroidery.css';
import './resources.css';
import './main.css';
import './tuxedo.css';
import { db } from '../firebase';
import { addData, deleteFileByUrl, getData, handleFireBaseUpload, updateDoc } from '../Firebase/firestore';
import sh from '../assets/search.png';

const TYPES = [
    { value: 'machine', label: 'Machine work' },
    { value: 'handwork', label: 'Handwork' },
    { value: 'mix', label: 'Hand & Machine Mix' },
];

// The thread colours the website's embroidery filter knows (src/Data/embroideryFinder.js
// on the site). Any other name still works: "Antique Gold" counts as Gold,
// "Sky Blue" as Blue, and so on.
const COLOR_SWATCHES = {
    Gold: 'linear-gradient(135deg, #f7e7b0 0%, #c9a227 45%, #8a6a12 100%)',
    Silver: 'linear-gradient(135deg, #ffffff 0%, #c4c7cc 45%, #7d8187 100%)',
    White: '#ffffff',
    Cream: '#efe6d2',
    Yellow: '#e8c547',
    Orange: '#e07b2e',
    Red: '#c0262d',
    Maroon: '#6e1b26',
    Pink: '#e48aa8',
    Purple: '#6d3f8c',
    Blue: '#2f6fbf',
    Navy: '#1d2a4d',
    Green: '#2f7a46',
    Brown: '#7a5233',
    Grey: '#8a8d91',
    Black: '#111111',
    Multicolour: 'conic-gradient(#c0262d, #e8c547, #2f7a46, #2f6fbf, #6d3f8c, #c0262d)',
};
const DEFAULT_COLOR_OPTIONS = Object.keys(COLOR_SWATCHES);
const SWATCH_WORDS = [
    [/gold|zari|metallic/, 'Gold'], [/silver/, 'Silver'], [/navy/, 'Navy'], [/maroon|wine|burgundy/, 'Maroon'],
    [/cream|ivory|beige|off.?white/, 'Cream'], [/white/, 'White'], [/yellow|mustard/, 'Yellow'], [/orange|peach|rust|copper/, 'Orange'],
    [/red|ruby|crimson/, 'Red'], [/pink|rose|magenta/, 'Pink'], [/purple|violet|lavender/, 'Purple'],
    [/blue|teal|turquoise/, 'Blue'], [/green|olive|emerald|mint/, 'Green'], [/brown|tan|coffee/, 'Brown'],
    [/grey|gray|charcoal/, 'Grey'], [/black/, 'Black'], [/multi/, 'Multicolour'],
];
const swatchFor = (name) => {
    const n = String(name || '').toLowerCase();
    const hit = SWATCH_WORDS.find(([re]) => re.test(n));
    return hit ? COLOR_SWATCHES[hit[1]] : '#dddddd';
};
const DEFAULT_CATEGORY_OPTIONS = [
    'Floral',
    'Religious',
    'Political',
    'Sports',
    'Corporate',
    'Wedding',
    'Monogram',
    'Traditional',
    'Modern',
    'Patriotic',
    'Military',
    'Cartoon',
    'Luxury',
];

// The website shows one card per design and opens its colours under it, so each
// style says which design it belongs to (`design`) and what its colour is called
// (`shade`). "Fill from name" reads them off the name the same way the website
// does when they are blank: colour words and a trailing number are the shade.
const COLOUR_WORDS = [
    'royal blue', 'dark teal', 'sky blue', 'navy blue', 'off white', 'rose gold',
    'golden', 'gold', 'black', 'white', 'cream', 'ivory', 'beige', 'red', 'crimson', 'maroon',
    'green', 'silver', 'teal', 'blue', 'aqua', 'pink', 'purple', 'mustard', 'navy',
    'yellow', 'orange', 'grey', 'gray', 'multi', 'multicolour', 'multicolor',
];
const COLOUR_RE = new RegExp(`\\b(${COLOUR_WORDS.map((w) => w.replace(' ', '\\s+')).join('|')})\\b`, 'gi');
const titleCase = (v) => v.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const designFromName = (rawName) => {
    const name = String(rawName || '').trim();
    if (!name || /^\d+$/.test(name)) return { design: name, shade: '' };
    const number = (name.match(/\s(\d+)$/) || [])[1];
    const rest = number ? name.slice(0, -number.length) : name;
    const colours = rest.match(COLOUR_RE) || [];
    const design = rest.replace(COLOUR_RE, ' ').replace(/\s&\s/g, ' ').replace(/\s+/g, ' ').replace(/^\s*&\s*|\s*&\s*$/g, '').trim();
    if (!design) return { design: titleCase(name), shade: '' };
    const colourText = colours.map(titleCase).join(' & ');
    const shade = colourText ? (number ? `${colourText} ${number}` : colourText) : (number ? `No. ${number}` : '');
    return { design: titleCase(design), shade };
};

const Embroidery_style = () => {
    const [formData, setFormData] = useState({
        name: '',
        design: '',
        shade: '',
        displayImage: null,
        description: '',
        type: '',
        otherImages: [],
    });

    const [displayImagePreview, setDisplayImagePreview] = useState(null);
    const [otherImagePreviews, setOtherImagePreviews] = useState([]);
    const [embroideryList, setEmbroideryList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadTotal, setUploadTotal] = useState(0);
    const [uploadStatus, setUploadStatus] = useState('');
    const [successModal, setSuccessModal] = useState(null);
    const [deletingUpload, setDeletingUpload] = useState(false);

    const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
    const typeDropdownRef = useRef(null);
    const [colorOptions, setColorOptions] = useState(DEFAULT_COLOR_OPTIONS);
    const [selectedColors, setSelectedColors] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState(DEFAULT_CATEGORY_OPTIONS);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [optionModal, setOptionModal] = useState({ open: false, label: '', value: '' });

    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');

    const [editingStyle, setEditingStyle] = useState(null);
    const [existingOtherUrls, setExistingOtherUrls] = useState([]);
    const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
    const [deletingCard, setDeletingCard] = useState(false);

    const normalizeOptionLabel = (value) => String(value || '')
        .trim()
        .split(' ')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');

    const parseStoredColors = (value) => {
        if (Array.isArray(value)) {
            return value.map(normalizeOptionLabel).filter(Boolean);
        }
        if (typeof value === 'string') {
            return value.split(',').map(normalizeOptionLabel).filter(Boolean);
        }
        return [];
    };

    const parseStoredCategories = (value) => {
        if (Array.isArray(value)) {
            return value.map(normalizeOptionLabel).filter(Boolean);
        }
        if (typeof value === 'string') {
            return value.split(',').map(normalizeOptionLabel).filter(Boolean);
        }
        return [];
    };

    const ensureColorOptions = (colors) => {
        setColorOptions((prev) => {
            const next = [...prev];
            colors.forEach((color) => {
                if (!color) return;
                if (!next.some((item) => item.toLowerCase() === color.toLowerCase())) {
                    next.push(color);
                }
            });
            return next;
        });
    };

    const ensureCategoryOptions = (categories) => {
        setCategoryOptions((prev) => {
            const next = [...prev];
            categories.forEach((category) => {
                if (!category) return;
                if (!next.some((item) => item.toLowerCase() === category.toLowerCase())) {
                    next.push(category);
                }
            });
            return next;
        });
    };

    const toggleColorOption = (name) => {
        setSelectedColors((prev) => {
            const exists = prev.some((item) => item.toLowerCase() === name.toLowerCase());
            if (exists) {
                return prev.filter((item) => item.toLowerCase() !== name.toLowerCase());
            }
            return [...prev, name];
        });
    };

    const toggleCategoryOption = (name) => {
        setSelectedCategories((prev) => {
            const exists = prev.some((item) => item.toLowerCase() === name.toLowerCase());
            if (exists) {
                return prev.filter((item) => item.toLowerCase() !== name.toLowerCase());
            }
            return [...prev, name];
        });
    };

    const addCustomColor = (value) => {
        const normalized = normalizeOptionLabel(value);
        if (!normalized) return false;

        ensureColorOptions([normalized]);
        setSelectedColors((prev) => {
            if (prev.some((item) => item.toLowerCase() === normalized.toLowerCase())) {
                return prev;
            }
            return [...prev, normalized];
        });
        return true;
    };

    const cancelEdit = () => {
        setEditingStyle(null);
        setExistingOtherUrls([]);
        setFormData({ name: '', design: '', shade: '', displayImage: null, description: '', type: '', otherImages: [] });
        setDisplayImagePreview(null);
        setOtherImagePreviews([]);
        setSelectedColors([]);
        setSelectedCategories([]);
        setOptionModal({ open: false, label: '', value: '' });
    };

    const startEdit = (item) => {
        const itemColors = parseStoredColors(item.color);
        const itemCategories = parseStoredCategories(item.category);
        setEditingStyle({ ...item });
        setExistingOtherUrls([...(item.other_images || [])]);
        setFormData({
            name: item.name || '',
            design: item.design || '',
            shade: item.shade || '',
            displayImage: null,
            description: item.description || '',
            type: item.type || '',
            otherImages: [],
        });
        setDisplayImagePreview(item.src || null);
        setOtherImagePreviews([]);
        setSelectedColors(itemColors);
        setSelectedCategories(itemCategories);
        ensureColorOptions(itemColors);
        ensureCategoryOptions(itemCategories);
        setTypeDropdownOpen(false);
    };

    const removeExistingOtherUrl = (index) => {
        setExistingOtherUrls((prev) => prev.filter((_, i) => i !== index));
    };

    const deleteStyleEntry = async (item) => {
        const id = item.id || item.doc;
        if (!id) return;
        const urls = [item.src, ...(item.other_images || [])].filter(Boolean);
        await Promise.all(urls.map((url) => deleteFileByUrl(url)));
        const coll = db.collection('Fabric').doc('embroidery').collection('styles');
        await coll.doc(id).delete();
    };

    const handleDeleteFromList = async () => {
        if (!deleteConfirmItem) return;
        try {
            setDeletingCard(true);
            await deleteStyleEntry(deleteConfirmItem);
            setDeleteConfirmItem(null);
            setEmbroideryList((prev) => (prev || []).filter((x) => x.id !== deleteConfirmItem.id));
            if (editingStyle && String(editingStyle.id) === String(deleteConfirmItem.id)) {
                cancelEdit();
            }
        } catch (err) {
            console.log('delete embroidery style from list', err);
            alert('Unable to delete this style.');
        } finally {
            setDeletingCard(false);
        }
    };

    useEffect(() => {
        const coll_ref = db.collection('Fabric').doc('embroidery').collection('styles');
        getData(coll_ref).then((emb) => {
            setEmbroideryList(emb || []);
        });
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
                setTypeDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNameChange = (e) => setFormData({ ...formData, name: e.target.value });
    const handleDescriptionChange = (e) => setFormData({ ...formData, description: e.target.value });

    const handleDisplayImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prev) => ({ ...prev, displayImage: file }));
            setDisplayImagePreview(URL.createObjectURL(file));
        }
    };

    const handleOtherImagesChange = (e) => {
        const files = Array.from(e.target.files);
        setFormData((prev) => ({ ...prev, otherImages: [...prev.otherImages, ...files] }));
        const previews = files.map((file) => URL.createObjectURL(file));
        setOtherImagePreviews((prev) => [...prev, ...previews]);
    };

    const removeOtherImage = (index) => {
        const newImages = formData.otherImages.filter((_, i) => i !== index);
        const newPreviews = otherImagePreviews.filter((_, i) => i !== index);
        setFormData({ ...formData, otherImages: newImages });
        setOtherImagePreviews(newPreviews);
    };

    const getTypeLabel = (value) => {
        const found = TYPES.find((t) => t.value === value);
        return found ? found.label : '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { name, displayImage, type } = formData;

        if (!name || !type) {
            alert('Please fill in all required fields.');
            return;
        }

        if (!editingStyle && !displayImage) {
            alert('Display image is required.');
            return;
        }

        if (editingStyle && !displayImage && !editingStyle.src) {
            alert('Display image is missing.');
            return;
        }

        const total = Math.max(
            1,
            (editingStyle ? (displayImage ? 1 : 0) : 1) + (formData.otherImages?.length || 0)
        );
        setUploadTotal(total);
        setUploadProgress(0);
        setLoading(true);

        try {
            if (editingStyle) {
                const id = String(editingStyle.id || editingStyle.doc);
                const storageBase = `Embroidery_styles/${id}`;
                let icon_url = editingStyle.src;

                if (displayImage) {
                    setUploadStatus('Uploading display image...');
                    if (editingStyle.src) {
                        try {
                            await deleteFileByUrl(editingStyle.src);
                        } catch (err) {
                            console.log('old icon delete skipped', err);
                        }
                    }
                    icon_url = await handleFireBaseUpload(`${id}_icon`, displayImage, storageBase);
                    setUploadProgress((p) => p + 1);
                }

                const removedOther = (editingStyle.other_images || []).filter((u) => !existingOtherUrls.includes(u));
                for (const url of removedOther) {
                    try {
                        await deleteFileByUrl(url);
                    } catch (err) {
                        console.log('other image delete skipped', err);
                    }
                }

                setUploadStatus('Uploading other images...');
                const newOtherUrls = [];
                for (let i = 0; i < formData.otherImages.length; i++) {
                    const url = await handleFireBaseUpload(`${id}_resource_${Date.now()}_${i}`, formData.otherImages[i], storageBase);
                    newOtherUrls.push(url);
                    setUploadProgress((p) => p + 1);
                }

                const other_images = [...existingOtherUrls, ...newOtherUrls];
                setUploadStatus('Saving to database...');
                const coll = db.collection('Fabric').doc('embroidery').collection('styles');
                const payload = {
                    id,
                    name: formData.name,
                    design: (formData.design || '').trim(),
                    shade: (formData.shade || '').trim(),
                    src: icon_url,
                    description: formData.description,
                    type: formData.type,
                    color: selectedColors,
                    category: selectedCategories,
                    other_images,
                    ...(editingStyle.created ? { created: editingStyle.created } : {}),
                };
                setUploadProgress((p) => Math.max(p, 1));
                await updateDoc(coll, id, payload);

                setEmbroideryList((prev) =>
                    (prev || []).map((x) => (String(x.id) === id ? { ...x, ...payload } : x))
                );

                setSuccessModal({
                    id,
                    name: formData.name,
                    type: getTypeLabel(formData.type) || formData.type,
                    color: selectedColors,
                    category: selectedCategories,
                    description: formData.description,
                    otherCount: other_images.length,
                    iconUrl: icon_url,
                    otherUrls: other_images,
                    isUpdate: true,
                });

                cancelEdit();
            } else {
                const id = new Date().getTime().toString();
                const storageBase = `Embroidery_styles/${id}`;

                setUploadStatus('Uploading display image...');
                const icon_url = await handleFireBaseUpload(`${id}_icon`, displayImage, storageBase);
                setUploadProgress((p) => p + 1);

                setUploadStatus('Uploading other images...');
                const resource_urls = [];
                for (let i = 0; i < formData.otherImages.length; i++) {
                    const url = await handleFireBaseUpload(`${id}_resource_${i}`, formData.otherImages[i], storageBase);
                    resource_urls.push(url);
                    setUploadProgress((p) => p + 1);
                }

                setUploadStatus('Saving to database...');
                const newEntry = {
                    id,
                    name: formData.name,
                    design: (formData.design || '').trim(),
                    shade: (formData.shade || '').trim(),
                    src: icon_url,
                    description: formData.description,
                    type: formData.type,
                    color: selectedColors,
                    category: selectedCategories,
                    other_images: resource_urls,
                };

                const coll = db.collection('Fabric').doc('embroidery').collection('styles');
                await addData(coll, id, newEntry);

                setEmbroideryList((prev) => [newEntry, ...prev]);

                setSuccessModal({
                    id,
                    name: formData.name,
                    type: getTypeLabel(formData.type) || formData.type,
                    color: selectedColors,
                    category: selectedCategories,
                    description: formData.description,
                    otherCount: resource_urls.length,
                    iconUrl: icon_url,
                    otherUrls: resource_urls,
                    isUpdate: false,
                });

                setFormData({ name: '', design: '', shade: '', displayImage: null, description: '', type: '', otherImages: [] });
                setDisplayImagePreview(null);
                setOtherImagePreviews([]);
                setSelectedColors([]);
                setSelectedCategories([]);
            }
        } catch (err) {
            console.log('Embroidery style upload failed:', err);
            alert(editingStyle != null ? 'Update failed. Please try again.' : 'Upload failed. Please try again.');
        } finally {
            setLoading(false);
            setUploadStatus('');
        }
    };

    const deleteUploadedEmbroideryStyle = async () => {
        if (!successModal) return;
        try {
            setDeletingUpload(true);
            const urls = [
                ...(successModal.iconUrl ? [successModal.iconUrl] : []),
                ...(successModal.otherUrls || []),
            ];
            await Promise.all(urls.map((url) => deleteFileByUrl(url)));

            const coll = db.collection('Fabric').doc('embroidery').collection('styles');
            await coll.doc(successModal.id).delete();

            setEmbroideryList((prev) => (prev || []).filter((item) => item.id !== successModal.id));
            setSuccessModal(null);
        } catch (error) {
            console.log('delete uploaded embroidery style', error);
            alert('Unable to delete the uploaded embroidery style.');
        } finally {
            setDeletingUpload(false);
        }
    };

    const knownDesigns = [...new Set((embroideryList || [])
        .map((item) => String(item.design || designFromName(item.name).design || '').trim())
        .filter((d) => d && !/^\d+$/.test(d)))].sort();

    const fillFromName = () => {
        const guess = designFromName(formData.name);
        setFormData((prev) => ({ ...prev, design: prev.design || guess.design, shade: prev.shade || guess.shade }));
    };

    const filteredList = (embroideryList || []).filter((item) => {
        if (typeFilter !== 'all' && (item.type || '').toLowerCase() !== typeFilter.toLowerCase()) return false;
        if (search && !JSON.stringify(item).toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    return (
        <div className="button_upload_page button-upload-premium-page embroidery-style-page">
            {optionModal.open && (
                <div className="fabric-option-modal-backdrop">
                    <div className="fabric-option-modal">
                        <div className="fabric-option-modal-title">Add {optionModal.label}</div>
                        <input
                            className="fabric-option-modal-input"
                            value={optionModal.value}
                            onChange={(e) => setOptionModal((prev) => ({ ...prev, value: e.target.value }))}
                            placeholder={`Enter ${optionModal.label}`}
                        />
                        <div className="fabric-option-modal-actions">
                            <button
                                type="button"
                                className="fabric-option-modal-cancel"
                                onClick={() => setOptionModal({ open: false, label: '', value: '' })}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="fabric-option-modal-save"
                                onClick={() => {
                                    let saved = false;
                                    if (optionModal.label === 'color') {
                                        saved = addCustomColor(optionModal.value);
                                    }
                                    if (saved) {
                                        setOptionModal({ open: false, label: '', value: '' });
                                    }
                                }}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="button_upload_form fabric-modern button-upload-premium-form">
                {editingStyle && (
                    <div className="embroidery-render-edit-banner">
                        <div>
                            <strong>Editing style</strong>
                            <span>{editingStyle.name || editingStyle.id}</span>
                        </div>
                        <button type="button" className="embroidery-render-edit-cancel" onClick={cancelEdit}>
                            Cancel edit
                        </button>
                    </div>
                )}
                <form onSubmit={handleSubmit} className="button-upload-premium-fields">
                    <div className="f-inputs">
                        <label className="button-upload-label">Name <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            placeholder="Enter embroidery name"
                            value={formData.name}
                            onChange={handleNameChange}
                            required
                        />
                    </div>

                    <div className="f-inputs">
                        <label className="button-upload-label">
                            Design
                            <button type="button" className="embroidery-fill-from-name" onClick={fillFromName} disabled={!formData.name}>
                                Fill from name
                            </button>
                        </label>
                        <input
                            type="text"
                            list="embroidery-design-list"
                            placeholder="e.g. Darbar Crest (same for every colour of this design)"
                            value={formData.design}
                            onChange={(e) => setFormData({ ...formData, design: e.target.value })}
                        />
                        <datalist id="embroidery-design-list">
                            {knownDesigns.map((d) => <option key={d} value={d} />)}
                        </datalist>
                        <small className="embroidery-field-help">The website shows one card per design and opens its colours under it.</small>
                    </div>

                    <div className="f-inputs">
                        <label className="button-upload-label">Shade</label>
                        <input
                            type="text"
                            placeholder="e.g. Gold & Black, or No. 12"
                            value={formData.shade}
                            onChange={(e) => setFormData({ ...formData, shade: e.target.value })}
                        />
                    </div>

                    <div className="f-inputs" ref={typeDropdownRef}>
                        <label className="button-upload-label">Type <span className="text-red-500">*</span></label>
                        <div className="fabric-custom-select">
                            <button type="button" className="fabric-custom-select-trigger" onClick={() => setTypeDropdownOpen((v) => !v)}>
                                <span>{formData.type ? getTypeLabel(formData.type) : 'Select type'}</span>
                                <span className={`fabric-custom-select-arrow ${typeDropdownOpen ? 'open' : ''}`}>▾</span>
                            </button>
                            {typeDropdownOpen && (
                                <div className="fabric-custom-select-menu">
                                    {TYPES.map((t) => (
                                        <button
                                            type="button"
                                            key={t.value}
                                            className={`fabric-custom-select-option ${formData.type === t.value ? 'active' : ''}`}
                                            onClick={() => { setFormData({ ...formData, type: t.value }); setTypeDropdownOpen(false); }}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="f-inputs">
                        <label className="button-upload-label">Description</label>
                        <textarea
                            rows={3}
                            placeholder="Describe the embroidery style (optional)"
                            value={formData.description}
                            onChange={handleDescriptionChange}
                            className="embroidery-style-textarea"
                        />
                    </div>

                    <div className="f-inputs">
                        <div className="fabric-label-row">
                            <label className="button-upload-label">Colors</label>
                            <button
                                type="button"
                                className="fabric-add-option-btn"
                                onClick={() => setOptionModal({ open: true, label: 'color', value: '' })}
                                aria-label="Add custom color"
                            >
                                +
                            </button>
                        </div>
                        <div className="fabric-composition-grid">
                            {colorOptions.map((color) => {
                                const checked = selectedColors.some((item) => item.toLowerCase() === color.toLowerCase());
                                return (
                                    <div
                                        key={color}
                                        className={`fabric-composition-row ${checked ? 'fabric-composition-row-active' : ''}`}
                                    >
                                        <label className="fabric-composition-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleColorOption(color)}
                                            />
                                            <span className="fabric-composition-tick" aria-hidden="true">
                                                <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="4 10 8 14 16 6"></polyline>
                                                </svg>
                                            </span>
                                            <span
                                                aria-hidden="true"
                                                style={{ width: 14, height: 14, borderRadius: '50%', flex: 'none', background: swatchFor(color), boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.2)' }}
                                            />
                                            <span className="fabric-composition-name">{color}</span>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="fabric-composition-total">
                            {selectedColors.length > 0 ? (
                                <>
                                    <span>Selected:</span>
                                    <strong>{selectedColors.join(', ')}</strong>
                                </>
                            ) : (
                                <span className="fabric-composition-hint">Add colors to see them here.</span>
                            )}
                        </div>
                        <div className="fabric-composition-hint" style={{ marginTop: 6 }}>
                            Tick every thread colour in the work. These drive the colour filter on the website; a shade you add (e.g. Antique Gold) counts as its main colour.
                        </div>
                    </div>

                    <div className="f-inputs">
                        <div className="fabric-label-row">
                            <label className="button-upload-label">Category</label>
                        </div>
                        <div className="fabric-composition-grid">
                            {categoryOptions.map((category) => {
                                const checked = selectedCategories.some((item) => item.toLowerCase() === category.toLowerCase());
                                return (
                                    <div
                                        key={category}
                                        className={`fabric-composition-row ${checked ? 'fabric-composition-row-active' : ''}`}
                                    >
                                        <label className="fabric-composition-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleCategoryOption(category)}
                                            />
                                            <span className="fabric-composition-tick" aria-hidden="true">
                                                <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="4 10 8 14 16 6"></polyline>
                                                </svg>
                                            </span>
                                            <span className="fabric-composition-name">{category}</span>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="fabric-composition-total">
                            {selectedCategories.length > 0 ? (
                                <>
                                    <span>Selected:</span>
                                    <strong>{selectedCategories.join(', ')}</strong>
                                </>
                            ) : (
                                <span className="fabric-composition-hint">Select categories to see them here.</span>
                            )}
                        </div>
                        <div className="fabric-composition-hint" style={{ marginTop: 6 }}>
                            Shown as the Style filter on the website (Floral, Wedding, Luxury…). Where it goes comes from the collection names (Neck, Collar, Cuff, Sleeve…).
                        </div>
                    </div>

                    <div className="f-inputs">
                        <div className="fabric-file-title">
                            <b>
                                {editingStyle ? 'Replace display image (optional)' : 'Display Image '}
                                {!editingStyle && <span className="text-red-500">*</span>}
                            </b>
                        </div>
                        {editingStyle && displayImagePreview && !formData.displayImage && (
                            <div className="embroidery-style-current-display-hint">Current display — choose a file below to replace</div>
                        )}
                        <input
                            className="fabric-file-input"
                            type="file"
                            accept="image/*"
                            onChange={handleDisplayImageChange}
                            required={!editingStyle}
                        />
                        {displayImagePreview && (
                            <div className="button-upload-preview">
                                <img src={displayImagePreview} alt="Display preview" />
                            </div>
                        )}
                    </div>

                    <div className="f-inputs">
                        <div className="fabric-file-title"><b>Other Images</b></div>
                        {existingOtherUrls.length > 0 && (
                            <div className="tuxedo-inline-previews embroidery-style-existing-others">
                                {existingOtherUrls.map((url, idx) => (
                                    <div className="tuxedo-inline-preview-item" key={`existing-other-${idx}`}>
                                        <img src={url} alt={`existing-${idx}`} />
                                        <button type="button" className="tuxedo-inline-remove" onClick={() => removeExistingOtherUrl(idx)} aria-label="Remove">×</button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <input
                            className="fabric-file-input"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleOtherImagesChange}
                        />
                        {otherImagePreviews.length > 0 && (
                            <div className="tuxedo-inline-previews">
                                {otherImagePreviews.map((preview, idx) => (
                                    <div className="tuxedo-inline-preview-item" key={`other-${idx}`}>
                                        <img src={preview} alt={`other-${idx}`} />
                                        <button type="button" className="tuxedo-inline-remove" onClick={() => removeOtherImage(idx)} aria-label="Remove">×</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {loading ? (
                        <div className="outfit-upload-progress">
                            <div className="outfit-upload-progress-top">
                                <span>{uploadStatus || 'Uploading...'}</span>
                                <strong>{uploadTotal ? Math.floor((uploadProgress / uploadTotal) * 100) : 0}%</strong>
                            </div>
                            <div className="outfit-upload-progress-track">
                                <div
                                    className="outfit-upload-progress-fill"
                                    style={{ width: `${uploadTotal ? (uploadProgress / uploadTotal) * 100 : 0}%` }}
                                />
                            </div>
                            <div className="outfit-upload-progress-meta">
                                {uploadProgress} / {uploadTotal} files uploaded
                            </div>
                        </div>
                    ) : (
                        <button type="submit" className="fabric-submit-btn">
                            {editingStyle ? 'Update embroidery style' : 'Upload Embroidery'}
                        </button>
                    )}
                </form>
            </div>

            <div className="button-upload-list-shell">
                <h2 className="button-upload-list-title">Uploaded Embroideries</h2>
                <div className="button-upload-filterbar">
                    <div className="button-upload-filter-switcher">
                        <button
                            type="button"
                            className={`button-upload-filter-chip ${typeFilter === 'all' ? 'button-upload-filter-chip-active' : ''}`}
                            onClick={() => setTypeFilter('all')}
                        >
                            all
                        </button>
                        {TYPES.map((t) => (
                            <button
                                key={t.value}
                                type="button"
                                className={`button-upload-filter-chip ${typeFilter === t.value ? 'button-upload-filter-chip-active' : ''}`}
                                onClick={() => setTypeFilter(t.value)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    <div className="edit-fabric-search-box button-upload-search-box">
                        <input
                            className="search_fabric_clothes"
                            placeholder="Search embroidery"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <img src={sh} alt="Search" />
                    </div>
                </div>

                <div className="button_uploads button-upload-list">
                    {filteredList.length === 0 ? (
                        <div className="add-brand-empty">No embroidery styles uploaded yet.</div>
                    ) : (
                        filteredList.map((item, i) => (
                            <div className="button_upload_card button-upload-premium-card" key={item.id || i}>
                                <div className="button-upload-card-actions">
                                    <button type="button" className="button-upload-edit-icon-btn" onClick={() => startEdit(item)} aria-label="Edit embroidery style">
                                        ✎
                                    </button>
                                    <button type="button" className="button-upload-edit-icon-btn button-upload-delete-icon-btn" onClick={() => setDeleteConfirmItem(item)} aria-label="Delete embroidery style">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <polyline points="3 6 5 6 21 6" />
                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                            <path d="M10 11v6M14 11v6" />
                                            <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                                        </svg>
                                    </button>
                                </div>
                                <div className="button-upload-card-image-wrap">
                                    <img src={item.src} alt={item.name || 'Embroidery'} className="button-upload-card-image" />
                                </div>
                                <div className="button-upload-card-details">
                                    <div className="button-upload-card-name" title={item.name || '-'}>{item.name || 'Untitled'}</div>
                                    <div className="button-upload-card-specs">
                                        <div className="button-upload-spec-row">
                                            <span className="button-upload-spec-label">Design</span>
                                            <span className="button-upload-spec-value" title={item.design ? '' : 'Not set: the website reads it from the name'}>
                                                {item.design || <em className="embroidery-guess">{designFromName(item.name).design || '-'} (from name)</em>}
                                            </span>
                                        </div>
                                        {(item.shade || designFromName(item.name).shade) && (
                                            <div className="button-upload-spec-row">
                                                <span className="button-upload-spec-label">Shade</span>
                                                <span className="button-upload-spec-value">
                                                    {item.shade || <em className="embroidery-guess">{designFromName(item.name).shade} (from name)</em>}
                                                </span>
                                            </div>
                                        )}
                                        <div className="button-upload-spec-row">
                                            <span className="button-upload-spec-label">Type</span>
                                            <span className="button-upload-spec-value">{getTypeLabel(item.type) || item.type || '-'}</span>
                                        </div>
                                        {parseStoredColors(item.color).length > 0 && (
                                            <div className="button-upload-spec-row">
                                                <span className="button-upload-spec-label">Colors</span>
                                                <span className="button-upload-spec-value embroidery-style-desc-value">{parseStoredColors(item.color).join(', ')}</span>
                                            </div>
                                        )}
                                        {parseStoredCategories(item.category).length > 0 && (
                                            <div className="button-upload-spec-row">
                                                <span className="button-upload-spec-label">Category</span>
                                                <span className="button-upload-spec-value embroidery-style-desc-value">{parseStoredCategories(item.category).join(', ')}</span>
                                            </div>
                                        )}
                                        <div className="button-upload-spec-row">
                                            <span className="button-upload-spec-label">Others</span>
                                            <span className="button-upload-spec-value">{(item.other_images || []).length}</span>
                                        </div>
                                        {item.description && (
                                            <div className="button-upload-spec-row">
                                                <span className="button-upload-spec-label">Note</span>
                                                <span className="button-upload-spec-value embroidery-style-desc-value">{item.description}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {deleteConfirmItem && (
                <div className="outfit-success-modal-backdrop" onClick={() => !deletingCard && setDeleteConfirmItem(null)}>
                    <div className="outfit-success-modal" onClick={(ev) => ev.stopPropagation()}>
                        <div className="outfit-success-modal-badge outfit-success-modal-badge-danger">Confirm Delete</div>
                        <h3>Delete this embroidery style?</h3>
                        <div className="outfit-success-modal-grid">
                            <div><span>Name</span><strong>{deleteConfirmItem.name || '-'}</strong></div>
                            <div><span>Type</span><strong>{getTypeLabel(deleteConfirmItem.type) || deleteConfirmItem.type || '-'}</strong></div>
                            <div><span>Other Images</span><strong>{(deleteConfirmItem.other_images || []).length}</strong></div>
                        </div>
                        <button type="button" className="fabric-submit-btn outfit-success-close" onClick={() => setDeleteConfirmItem(null)} disabled={deletingCard}>
                            Cancel
                        </button>
                        <button type="button" className="fabric-submit-btn outfit-delete-close" onClick={handleDeleteFromList} disabled={deletingCard}>
                            {deletingCard ? 'Deleting...' : 'Delete'}
                        </button>
                    </div>
                </div>
            )}

            {successModal && (
                <div className="outfit-success-modal-backdrop">
                    <div className="outfit-success-modal">
                        <div className="outfit-success-modal-badge">{successModal.isUpdate ? 'Updated' : 'Upload Successful'}</div>
                        <h3>{successModal.isUpdate ? 'Embroidery style updated successfully' : 'Embroidery style uploaded successfully'}</h3>
                        <div className="outfit-success-modal-grid">
                            <div><span>ID</span><strong>{successModal.id}</strong></div>
                            <div><span>Name</span><strong>{successModal.name}</strong></div>
                            <div><span>Type</span><strong>{successModal.type}</strong></div>
                            {parseStoredColors(successModal.color).length > 0 && (
                                <div><span>Colors</span><strong>{parseStoredColors(successModal.color).join(', ')}</strong></div>
                            )}
                            {parseStoredCategories(successModal.category).length > 0 && (
                                <div><span>Category</span><strong>{parseStoredCategories(successModal.category).join(', ')}</strong></div>
                            )}
                            <div><span>Other Images</span><strong>{successModal.otherCount}</strong></div>
                            {successModal.description && (
                                <div><span>Description</span><strong>{successModal.description}</strong></div>
                            )}
                        </div>
                        <button type="button" className="fabric-submit-btn outfit-success-close" onClick={() => setSuccessModal(null)}>
                            Close
                        </button>
                        <button
                            type="button"
                            className="fabric-submit-btn outfit-delete-close"
                            onClick={() => {
                                if (deletingUpload) return;
                                if (window.confirm(`Are you sure you want to delete ${successModal?.name || 'this uploaded embroidery style'}? This action cannot be undone.`)) {
                                    deleteUploadedEmbroideryStyle();
                                }
                            }}
                            disabled={deletingUpload}
                        >
                            {deletingUpload ? 'Deleting...' : 'Delete This Upload'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Embroidery_style;
